import { useRef, useState } from "react";
import { Field, Formik, Form } from "formik";
import {
  Button,
  FullscreenModal,
  SelectComponent,
  Textarea,
  DropFileInput,
  SVG,
  Checkbox,
} from "shared";
import * as Yup from "yup";
import { SVG_ENUM, VALIDATION_ENUM } from "enums";
import {
  useAddSuccessorDoc,
  type SuccessorDocType,
} from "hooks/successors/AddSuccessorDocsHook";
import { useGetSuccessorsDocs } from "hooks/successors";
import "./AddDocument.scss";

interface AddDocumentProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const AddDocument: React.FC<AddDocumentProps> = ({ isOpen, onClose }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {
    data: { docs = [] } = {
      docs: [],
      canViewFiles: false,
    },
  } = useGetSuccessorsDocs({ enabled: true });
  const { mutate: addDoc } = useAddSuccessorDoc({
    onSuccess: () => {
      setIsSubmitting(false);
      onClose();
    },
    onError: (data: any) => {
      if (data?.data?.isAuthorized === false) {
        onClose();
      }
      setIsSubmitting(false);
    },
  });
  const formRef = useRef<any>(null);
  const isFirstDoc = !docs || docs.length === 0;

  type FormValues = {
    isAccessibleByReliance: boolean | null;
    type: SuccessorDocType | "";
    description: string;
    file: File | null;
  };

  const initialValues: FormValues = {
    isAccessibleByReliance: isFirstDoc ? false : null,
    type: "",
    description: "",
    file: null,
  };

  const onSubmit = async (values: FormValues) => {
    setIsSubmitting(true);
    const type = values.type as SuccessorDocType;

    const accessible = isFirstDoc ? values.isAccessibleByReliance : null;
    await addDoc({
      type,
      description: values.description || null,
      isAccessibleByReliance: accessible,
      file: values.file,
    });
  };

  const validation = () =>
    Yup.object().shape({
      type: Yup.string().required(VALIDATION_ENUM.REQUIRED),
      description: Yup.string().required(VALIDATION_ENUM.REQUIRED),
    });

  return (
    <FullscreenModal
      className="add-document__modal"
      isOpen={isOpen}
      onClose={onClose}
    >
      <h2 className="fullscreen-modal__header">Dodaj dokument/notatkę</h2>

      <div className="extra-info extra-info--add-document">
        <div className="extra-info__left">
          <SVG type={SVG_ENUM.INFO} className="extra-info__icon" />
        </div>
        <div className="extra-info__right">
          <strong>Prosimy nie umieszczać w tej sekcji poufnych danych,</strong>{" "}
          takich jak: kody dostępu, hasła, lokalizacja sejfów lub szczegóły
          dotyczące przechowywania majątku!
        </div>
      </div>

      <Formik
        initialValues={initialValues}
        onSubmit={(values) => onSubmit(values)}
        validationSchema={validation()}
        enableReinitialize
        innerRef={formRef}
      >
        {({ errors }) => (
          <Form className="m-width-100 add-document__form">
            <Field
              label="Temat"
              name="type"
              id="type"
              placeholder="Wybierz "
              as={SelectComponent}
              options={[
                {
                  label: "Testament",
                  value: "testament",
                },
                {
                  label: "Dokumenty notarialne",
                  value: "notarial_documents",
                },
                {
                  label: "Akt własności",
                  value: "deed_of_ownership",
                },
                {
                  label: "Notatki prywatne",
                  value: "note",
                },
              ]}
            />
            <Field
              type="textarea"
              id="description"
              name="description"
              label={"Opis"}
              component={Textarea}
              maxLength={254}
              errors={errors}
            />

            <Field
              label="Załącz plik"
              name="file"
              id="file"
              as={DropFileInput}
              accept=".png, .jpg, .pdf"
            />

            {isFirstDoc && (
              <Field
                id="isAccessibleByReliance"
                name="isAccessibleByReliance"
                wrapperClassName="login-wrapper__acceptance checkbox--white"
                label={
                  <p>
                    Udzielam zgody, by pracownicy firmy Reliance mieli dostęp do
                    dodawanych przeze mnie dokumentów.
                  </p>
                }
                as={Checkbox}
              />
            )}

            <div className="fullscreen-modal__buttons">
              <Button
                label="Anuluj"
                onClick={onClose}
                className="button--stroke button--rounded"
              />
              <Button
                label={"Potwierdź"}
                type="submit"
                onClick={() => null}
                className=" button--rounded button--black"
                isLoading={isSubmitting}
              />
            </div>
          </Form>
        )}
      </Formik>
    </FullscreenModal>
  );
};

export default AddDocument;
